import {
  ArrowRightIcon,
  ArrowsClockwiseIcon,
  BriefcaseIcon,
  GraduationCapIcon,
  IdentificationBadgeIcon,
  UserFocusIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";

import type { CareerProfile, ExperienceRange } from "../types";

type Props = {
  profile: CareerProfile | null;
  experience: ExperienceRange | null;
  onProfileChange: (value: CareerProfile) => void;
  onExperienceChange: (value: ExperienceRange) => void;
  onContinue: () => void;
};

const profiles = [
  {
    value: "student",
    label: "Étudiant(e) ou jeune diplômé(e)",
    icon: GraduationCapIcon,
  },
  {
    value: "first-job",
    label: "À la recherche d’un premier emploi",
    icon: BriefcaseIcon,
  },
  {
    value: "experienced",
    label: "Professionnel(le) expérimenté(e)",
    icon: IdentificationBadgeIcon,
  },
  {
    value: "career-change",
    label: "En reconversion professionnelle",
    icon: ArrowsClockwiseIcon,
  },
  {
    value: "independent",
    label: "Indépendant(e) ou consultant(e)",
    icon: UserFocusIcon,
  },
] satisfies Array<{
  value: CareerProfile;
  label: string;
  icon: typeof BriefcaseIcon;
}>;

const ranges = [
  {
    value: "none",
    label: "Aucune",
  },
  {
    value: "under-2",
    label: "Moins de 2 ans",
  },
  {
    value: "2-to-5",
    label: "2 à 5 ans",
  },
  {
    value: "over-5",
    label: "Plus de 5 ans",
  },
] satisfies Array<{
  value: ExperienceRange;
  label: string;
}>;

export const CareerStep = ({
  profile,
  experience,
  onProfileChange,
  onExperienceChange,
  onContinue,
}: Props) => {
  const complete = Boolean(profile && experience);

  return (
    <section className="mx-auto w-full max-w-3xl">
      <header>
        <h1
          id="create-resume-title"
          className={cn(
            "text-2xl font-bold tracking-tight",
            "leading-tight text-indigo-950",
            "sm:text-3xl lg:text-[34px]",
          )}
        >
          Où en êtes-vous dans votre parcours&nbsp;?
        </h1>

        <p className={cn("mt-1.5 text-xs leading-5 text-indigo-950/75", "sm:text-sm sm:leading-6")}>
          Cela nous aide à vous proposer des modèles et des conseils adaptés.
        </p>
      </header>

      <div
        role="radiogroup"
        aria-label="Profil professionnel"
        className={cn("mt-4 grid grid-cols-2 gap-2.5", "sm:grid-cols-3 sm:gap-3")}
      >
        {profiles.map(({ value, label, icon: IconComponent }) => {
          const active = profile === value;

          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => {
                onProfileChange(value);
              }}
              className={cn(
                "group relative flex min-h-[92px]",
                "flex-col items-center justify-center",
                "rounded-xl border-2 bg-white",
                "px-2.5 py-2.5 text-center",
                "transition-all duration-200",
                "hover:-translate-y-0.5",
                "hover:border-indigo-200 hover:shadow-md",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-indigo-500 focus-visible:ring-offset-1",
                "sm:min-h-[108px] sm:px-3",
                active
                  ? "border-emerald-400 bg-emerald-50/60 shadow-[0_6px_18px_rgba(16,185,129,0.09)]"
                  : "border-slate-200",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "absolute left-2 top-2 size-4",
                  "rounded-full border-2 bg-white",
                  active ? "border-[4px] border-emerald-500" : "border-transparent",
                )}
              />

              <span
                className={cn(
                  "grid size-9 shrink-0 place-items-center",
                  "rounded-xl",
                  "sm:size-10",
                  active ? "bg-emerald-100 text-emerald-600" : "bg-violet-50 text-indigo-700",
                )}
              >
                <IconComponent aria-hidden size={22} weight="duotone" className="sm:size-6" />
              </span>

              <span
                className={cn(
                  "mt-1.5 text-[10px] font-bold",
                  "leading-3.5 text-indigo-950",
                  "sm:text-xs sm:leading-4",
                )}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>

      <fieldset className={cn("mt-4", "sm:mt-5")}>
        <legend className={cn("text-xs font-bold text-indigo-950", "sm:text-sm")}>
          Années d’expérience
        </legend>

        <div className={cn("mt-2 grid grid-cols-2 gap-2", "sm:grid-cols-4 sm:gap-2.5")}>
          {ranges.map(({ value, label }) => {
            const active = experience === value;

            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => {
                  onExperienceChange(value);
                }}
                className={cn(
                  "relative min-h-10 rounded-xl border-2",
                  "px-2.5 text-[10px] font-semibold",
                  "transition-all duration-200",
                  "hover:-translate-y-0.5",
                  "focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-indigo-500 focus-visible:ring-offset-1",
                  "sm:min-h-11 sm:px-3 sm:text-xs",
                  active
                    ? "border-emerald-400 bg-emerald-50 text-indigo-950 shadow-[0_5px_14px_rgba(16,185,129,0.08)]"
                    : "border-slate-200 bg-white text-indigo-950 hover:border-indigo-200",
                )}
              >
                {active && (
                  <span
                    aria-hidden
                    className={cn(
                      "absolute left-2 top-1/2 size-3.5",
                      "-translate-y-1/2 rounded-full",
                      "border-[4px] border-emerald-500 bg-white",
                    )}
                  />
                )}

                {label}
              </button>
            );
          })}
        </div>
      </fieldset>

      <button
        type="button"
        disabled={!complete}
        onClick={onContinue}
        className={cn(
          "mx-auto mt-4 flex min-h-10 w-full max-w-[280px]",
          "items-center justify-center gap-3 rounded-xl",
          "bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500",
          "px-6 text-sm font-bold text-white",
          "shadow-[0_8px_20px_rgba(79,70,229,0.22)]",
          "transition-all duration-200",
          "hover:-translate-y-0.5",
          "hover:shadow-[0_11px_25px_rgba(79,70,229,0.28)]",
          "focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-40",
          "disabled:hover:translate-y-0 disabled:hover:shadow-none",
          "sm:mt-5 sm:min-h-11 sm:text-base",
        )}
      >
        Continuer
        <ArrowRightIcon aria-hidden size={19} weight="bold" className="shrink-0" />
      </button>
    </section>
  );
};
